import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';
import { z } from 'zod';
import {
  CalendarDays, CheckCircle2, ClipboardList, SendHorizonal, Clock, LogOut,
  CalendarOff, Wallet, Menu, X, Home,
  TrendingUp, UserCheck, XCircle, Loader2, Search,
  Users, Bell, ChevronLeft, ChevronRight, UserPlus
} from 'lucide-react';
import { getAuthHeaders, captureTokenFromURL, clearToken } from '../auth/token';

const CreateReq = z.object({ dates: z.array(z.date()).min(1), reason: z.string().min(3) });

type Role = 'Enrollment Specialist' | 'Senior Contract Specialist' | 'Manager' | 'Admin' | 'TeamAdmin' | 'Closer' | 'User';
type User = { id: string; name: string; email: string; role: Role; teamId?: string | null; teamName?: string | null };

// ─── Holidays ─────────────────────────────────────────────────────────────────
const COMPANY_HOLIDAYS_2025 = [
  { date: new Date(2025, 0, 1),  name: "New Year's Day",               dayOfWeek: 'Wednesday', year: 2025 },
  { date: new Date(2025, 4, 26), name: 'Memorial Day',                 dayOfWeek: 'Monday',    year: 2025 },
  { date: new Date(2025, 6, 4),  name: 'Independence Day',             dayOfWeek: 'Friday',    year: 2025 },
  { date: new Date(2025, 8, 1),  name: 'Labor Day',                    dayOfWeek: 'Monday',    year: 2025 },
  { date: new Date(2025, 10, 27),name: 'Thanksgiving Day',             dayOfWeek: 'Thursday',  year: 2025 },
  { date: new Date(2025, 10, 28),name: 'Day After Thanksgiving',       dayOfWeek: 'Friday',    year: 2025 },
  { date: new Date(2025, 11, 24),name: 'Christmas Eve',                dayOfWeek: 'Wednesday', year: 2025 },
  { date: new Date(2025, 11, 25),name: 'Christmas Day',                dayOfWeek: 'Thursday',  year: 2025 },
  { date: new Date(2025, 11, 31),name: "New Year's Eve",               dayOfWeek: 'Wednesday', year: 2025 },
];
const COMPANY_HOLIDAYS_2026 = [
  { date: new Date(2026, 0, 1),  name: "New Year's Day",               dayOfWeek: 'Thursday',  year: 2026 },
  { date: new Date(2026, 4, 25), name: 'Memorial Day',                 dayOfWeek: 'Monday',    year: 2026 },
  { date: new Date(2026, 6, 3),  name: 'Independence Day (Observed)',  dayOfWeek: 'Friday',    year: 2026 },
  { date: new Date(2026, 6, 4),  name: 'Independence Day',             dayOfWeek: 'Saturday',  year: 2026 },
  { date: new Date(2026, 8, 7),  name: 'Labor Day',                    dayOfWeek: 'Monday',    year: 2026 },
  { date: new Date(2026, 10, 26),name: 'Thanksgiving Day',             dayOfWeek: 'Thursday',  year: 2026 },
  { date: new Date(2026, 10, 27),name: 'Day After Thanksgiving',       dayOfWeek: 'Friday',    year: 2026 },
  { date: new Date(2026, 11, 24),name: 'Christmas Eve',                dayOfWeek: 'Thursday',  year: 2026 },
  { date: new Date(2026, 11, 25),name: 'Christmas Day',                dayOfWeek: 'Friday',    year: 2026 },
  { date: new Date(2026, 11, 31),name: "New Year's Eve",               dayOfWeek: 'Thursday',  year: 2026 },
];
const ALL_HOLIDAYS = [...COMPANY_HOLIDAYS_2025, ...COMPANY_HOLIDAYS_2026];

// ─── Types ────────────────────────────────────────────────────────────────────
type CalendarEntry = { id: string; userId: string; userName: string; dates: string[]; status: string; submittedAt?: string };
type PendingItem = { id: string; employeeName?: string; employeeEmail?: string; date: string; reason: string; submittedAt?: string; teamName?: string; dates?: string[]; duration_type?: string; duration_hours?: number | null };
type ADPBalance = { timeOffTypeName: string; balance: number; used: number; units: string };
type EmployeeBalances = { email: string; fullName: string; balances: ADPBalance[] };
type MyRequestItem = { id: string; date: string; reason: string; status: 'PENDING' | 'APPROVED' | 'REJECTED'; submittedAt?: string; decidedAt?: string; decisionNote?: string; dates?: string[]; duration_type?: string; duration_hours?: number | null };
type TeamMember = { id: string; email: string; full_name: string; role: string };
type EmployeeOption = { id: string; email: string; full_name: string; role: string; team_name?: string | null };
type AttendanceRecordItem = { employee_name: string; employee_email: string; missed_days: number };
type AttendanceRecordData = {
  period: { start: string; end: string; label: string; kind: '25-9' | '10-24' };
  items: AttendanceRecordItem[];
};
type AttendancePeriodView = 'current' | 'previous';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const AZ_TZ = 'America/Phoenix';

function fmtTimeLocal(iso?: string) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', { timeZone: AZ_TZ, month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}
function fmtMDY(isoDate: string) {
  const m = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return isoDate;
  return `${m[2]}/${m[3]}/${m[1]}`;
}
function fmtDateRange(isoDates: string[]) {
  if (!isoDates?.length) return '';
  const u = Array.from(new Set(isoDates.map(s => s.slice(0, 10)))).sort();
  return u.length > 1 ? `${fmtMDY(u[0])} – ${fmtMDY(u[u.length - 1])}` : fmtMDY(u[0]);
}
function normalizeBalances(raw: any[]): ADPBalance[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((b: any) => ({
    timeOffTypeName: b.timeOffTypeName ?? b.timeOffType?.nameCode?.shortName ?? b.typeName ?? b.name ?? 'PTO',
    balance: Number(b.balance ?? b.availableBalance ?? b.remainingBalance ?? 0),
    used: Number(b.used ?? b.usedBalance ?? b.takenBalance ?? 0),
    units: b.unitCode?.shortName ?? b.units ?? 'Hours',
  }));
}
function isAdminLike(role: Role) { return ['Admin', 'Manager'].includes(role); }
function canApprove(role: Role) { return ['Admin', 'Manager', 'TeamAdmin'].includes(role); }
function isEmployee(role: Role) { return !canApprove(role); }

// ─── Avatar helpers ───────────────────────────────────────────────────────────
const AVATAR_COLORS = [
  'from-yellow-500 to-yellow-600', 'from-emerald-500 to-emerald-600',
  'from-blue-500 to-blue-600',     'from-purple-500 to-purple-600',
  'from-pink-500 to-pink-600',     'from-orange-500 to-orange-600',
  'from-teal-500 to-teal-600',     'from-red-500 to-red-600',
];
function avatarColor(name: string) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffffffff;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}
function initials(name: string) {
  return name.split(/\s+/).map(p => p[0]).join('').slice(0, 2).toUpperCase() || '?';
}

// ─── Shared UI components (defined OUTSIDE main component to avoid remount) ──
function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    APPROVED: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    REJECTED: 'bg-red-500/15 text-red-400 border-red-500/30',
    PENDING:  'bg-amber-500/15 text-amber-400 border-amber-500/30',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${map[status] ?? 'bg-slate-700 text-slate-300 border-slate-600'}`}>
      {status}
    </span>
  );
}

function DurationBadge({ duration_type, duration_hours }: { duration_type?: string; duration_hours?: number | null }) {
  if (!duration_type || duration_type === 'full_day') {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-500/15 text-yellow-300 border border-yellow-500/30">☀️ Full Day</span>;
  }
  if (duration_type === 'half_day') {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/15 text-blue-300 border border-blue-500/30">🌤 Half Day</span>;
  }
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-500/15 text-purple-300 border border-purple-500/30">⏱ {duration_hours} hrs</span>;
}

function StatCard({ label, value, icon: Icon, color }: { label: string; value: number | string; icon: any; color: string }) {
  return (
    <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-5 flex items-center gap-4">
      <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
        <Icon className="h-6 w-6" />
      </div>
      <div>
        <div className="text-2xl font-bold text-white">{value}</div>
        <div className="text-sm text-slate-400">{label}</div>
      </div>
    </div>
  );
}

function SectionHeader({ icon: Icon, title, subtitle, color = 'text-yellow-400' }: { icon: any; title: string; subtitle?: string; color?: string }) {
  return (
    <div className="flex items-center gap-3 mb-6">
      <div className="w-10 h-10 rounded-xl bg-[#2a2a2a] flex items-center justify-center flex-shrink-0">
        <Icon className={`h-5 w-5 ${color}`} />
      </div>
      <div>
        <h2 className="text-lg font-semibold text-white">{title}</h2>
        {subtitle && <p className="text-sm text-slate-400">{subtitle}</p>}
      </div>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="w-16 h-16 rounded-full bg-[#2a2a2a] flex items-center justify-center mb-4">
        <ClipboardList className="h-8 w-8 text-slate-500" />
      </div>
      <p className="text-slate-400 text-sm">{message}</p>
    </div>
  );
}

// ─── Team Calendar (defined outside to avoid remount) ────────────────────────
function TeamCalendarView({ calendar }: { calendar: CalendarEntry[] }) {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [tooltip, setTooltip] = useState<{ key: string; x: number; y: number; cellTop: number } | null>(null);

  const dayMap = useMemo(() => {
    const m: Record<string, { name: string; status: string }[]> = {};
    for (const entry of calendar) {
      for (const d of entry.dates) {
        const key = d.slice(0, 10);
        if (!m[key]) m[key] = [];
        if (!m[key].find(e => e.name === entry.userName)) {
          m[key].push({ name: entry.userName, status: entry.status });
        }
      }
    }
    return m;
  }, [calendar]);

  const holidayMap = useMemo(() => {
    const m: Record<string, string> = {};
    for (const h of ALL_HOLIDAYS) {
      const key = `${h.date.getFullYear()}-${String(h.date.getMonth() + 1).padStart(2, '0')}-${String(h.date.getDate()).padStart(2, '0')}`;
      m[key] = h.name;
    }
    return m;
  }, []);

  function prevMonth() {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  }
  function nextMonth() {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  }

  const monthName = new Date(viewYear, viewMonth, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' });
  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-white">Team Calendar</h2>
          <p className="text-sm text-slate-400">Company-wide time off at a glance</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={prevMonth} className="p-2 rounded-xl bg-[#111] border border-[#2a2a2a] text-slate-400 hover:text-white hover:border-[#3a3a3a] transition-colors">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-sm font-semibold text-white min-w-[140px] text-center">{monthName}</span>
          <button onClick={nextMonth} className="p-2 rounded-xl bg-[#111] border border-[#2a2a2a] text-slate-400 hover:text-white hover:border-[#3a3a3a] transition-colors">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
      {/* Legend */}
      <div className="flex gap-4 mb-4">
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-emerald-400"></div><span className="text-xs text-slate-400">Approved</span></div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-amber-400 opacity-60"></div><span className="text-xs text-slate-400">Pending</span></div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-red-500/70"></div><span className="text-xs text-slate-400">Holiday</span></div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-yellow-400"></div><span className="text-xs text-slate-400">Today</span></div>
      </div>
      {/* Day headers */}
      <div className="grid grid-cols-7 mb-2">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
          <div key={d} className="text-center text-xs font-semibold text-slate-500 py-2">{d}</div>
        ))}
      </div>
      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, idx) => {
          if (day === null) return <div key={`empty-${idx}`} />;
          const key = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const people = dayMap[key] || [];
          const holiday = holidayMap[key];
          const isToday = key === todayStr;
          const isWeekend = [0, 6].includes(new Date(viewYear, viewMonth, day).getDay());
          const hasPeople = people.length > 0;
          return (
            <div
              key={key}
              className={`relative min-h-[72px] rounded-xl p-2 border transition-all cursor-default ${
                isToday
                  ? 'border-yellow-400/60 bg-yellow-500/10'
                  : holiday
                  ? 'border-red-500/20 bg-red-500/5'
                  : isWeekend
                  ? 'border-[#1e1e1e] bg-[#111]/50'
                  : hasPeople
                  ? 'border-[#2a2a2a] bg-[#111] hover:border-[#3a3a3a]'
                  : 'border-[#1e1e1e] bg-[#111]/30'
              }`}
              onMouseEnter={hasPeople || holiday ? (e) => {
                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                setTooltip({ key, x: rect.left, y: rect.bottom, cellTop: rect.top });
              } : undefined}
              onMouseLeave={() => setTooltip(null)}
            >
              <div className={`text-xs font-semibold mb-1 ${isToday ? 'text-yellow-400' : isWeekend ? 'text-slate-600' : 'text-slate-400'}`}>
                {day}
              </div>
              {holiday && (
                <div className="text-[9px] leading-tight text-red-400 font-medium truncate mb-1">{holiday}</div>
              )}
              {hasPeople && (
                <div className="flex flex-wrap gap-0.5 mt-1">
                  {people.slice(0, 6).map((p, i) => (
                    <div
                      key={i}
                      className={`w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold text-white bg-gradient-to-br ${avatarColor(p.name)} ${p.status === 'PENDING' ? 'opacity-60 ring-1 ring-amber-400' : ''}`}
                      title={p.name}
                    >
                      {initials(p.name)}
                    </div>
                  ))}
                  {people.length > 6 && (
                    <div className="w-5 h-5 rounded-full bg-[#333] flex items-center justify-center text-[8px] text-slate-400 font-bold">
                      +{people.length - 6}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* Tooltip */}
      {tooltip && (() => {
        const people = dayMap[tooltip.key] || [];
        const holiday = holidayMap[tooltip.key];
        if (!people.length && !holiday) return null;
        const [y, m, d] = tooltip.key.split('-');
        const dateLabel = new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
        return (
          <div
            className="fixed z-50 bg-[#1e1e1e] border border-[#3a3a3a] rounded-xl shadow-2xl p-4 min-w-[200px] max-w-[260px] pointer-events-none"
            style={(() => {
              const spaceBelow = window.innerHeight - tooltip.y;
              const openUp = spaceBelow < 220;
              return {
                left: Math.min(Math.max(tooltip.x, 8), window.innerWidth - 280),
                ...(openUp
                  ? { bottom: window.innerHeight - tooltip.cellTop + 8 }
                  : { top: tooltip.y + 8 })
              };
            })()}
          >
            <div className="text-xs font-semibold text-slate-300 mb-3">{dateLabel}</div>
            {holiday && <div className="text-xs text-red-400 mb-2 font-medium">🏖 {holiday}</div>}
            {people.length > 0 && (
              <ul className="space-y-1.5">
                {people.map((p, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <div className={`w-6 h-6 rounded-full bg-gradient-to-br ${avatarColor(p.name)} flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0`}>
                      {initials(p.name)}
                    </div>
                    <span className="text-xs text-slate-300">{p.name}</span>
                    <span className={`ml-auto text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${p.status === 'APPROVED' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
                      {p.status}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })()}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function TimeOffPage() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string>('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Request form state
  const [selected, setSelected] = useState<Date[]>([]);
  const [reason, setReason] = useState('');
  const [durationType, setDurationType] = useState<'full_day' | 'half_day' | 'custom'>('full_day');
  const [durationHours, setDurationHours] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Data state
  const [calendar, setCalendar] = useState<CalendarEntry[]>([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [myRequests, setMyRequests] = useState<MyRequestItem[]>([]);
  const [pending, setPending] = useState<PendingItem[]>([]);
  const [myBalances, setMyBalances] = useState<ADPBalance[]>([]);
  const [allBalances, setAllBalances] = useState<EmployeeBalances[]>([]);
  const [balancesLoading, setBalancesLoading] = useState(false);
  const [balancesLinked, setBalancesLinked] = useState(true);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [attendanceRecord, setAttendanceRecord] = useState<AttendanceRecordData | null>(null);
  const [attendanceRecordLoading, setAttendanceRecordLoading] = useState(false);
  const [attendancePeriodView, setAttendancePeriodView] = useState<AttendancePeriodView>('current');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');

  // Admin-insert state
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [adminInsertEmployee, setAdminInsertEmployee] = useState<string>(''); // employee id
  const [adminInsertSelected, setAdminInsertSelected] = useState<Date[]>([]);
  const [adminInsertReason, setAdminInsertReason] = useState('');
  const [adminInsertDurationType, setAdminInsertDurationType] = useState<'full_day' | 'half_day' | 'custom'>('full_day');
  const [adminInsertDurationHours, setAdminInsertDurationHours] = useState('');
  const [adminInsertStatus, setAdminInsertStatus] = useState<'APPROVED' | 'PENDING'>('APPROVED');
  const [adminInsertSubmitting, setAdminInsertSubmitting] = useState(false);
  const [adminInsertSearch, setAdminInsertSearch] = useState('');

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const upcomingHolidays = ALL_HOLIDAYS.filter(h => h.date >= today);
  const notifCount = pending.length;

  // ─── Auth & Bootstrap ──────────────────────────────────────────────────────
  useEffect(() => {
    captureTokenFromURL();
    fetch('/api/me', { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.id) {
          setUser({ id: d.id, name: d.fullName || d.email, email: d.email, role: d.role || 'Enrollment Specialist', teamId: d.teamId, teamName: d.teamName });
          setActiveTab('dashboard');
        } else {
          window.location.href = '/api/auth/login?returnTo=/time-off';
        }
        setLoading(false);
      })
      .catch(() => { window.location.href = '/api/auth/login?returnTo=/time-off'; });
  }, []);

  // ─── Load Calendar ─────────────────────────────────────────────────────────
  const loadCalendar = useCallback(() => {
    if (!user) return;
    setCalendarLoading(true);
    const from = new Date(Date.now() - 30 * 864e5).toISOString().slice(0, 10);
    const to = new Date(Date.now() + 120 * 864e5).toISOString().slice(0, 10);
    fetch(`/api/time-off/calendar?from=${from}&to=${to}`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then((d: any) => {
        if (Array.isArray(d?.entries)) {
          setCalendar(d.entries.map((e: any) => ({
            id: e.id,
            userId: e.userId || e.user_id || '',
            userName: e.name || e.userName || '',
            dates: Array.isArray(e.dates) ? e.dates : [],
            status: e.status,
            submittedAt: e.created_at,
          })));
        }
      }).catch(() => {}).finally(() => setCalendarLoading(false));
  }, [user]);

  // ─── Load My Requests ──────────────────────────────────────────────────────
  const loadMyRequests = useCallback(() => {
    if (!user) return;
    fetch('/api/time-off/mine', { headers: getAuthHeaders() })
      .then(r => r.json())
      .then((d: any) => {
        if (Array.isArray(d?.items)) {
          setMyRequests(d.items.map((item: any) => ({
            id: item.id,
            date: Array.isArray(item.dates) && item.dates.length ? fmtDateRange(item.dates) : '—',
            dates: item.dates,
            reason: item.reason ?? '',
            status: item.status ?? 'PENDING',
            submittedAt: item.created_at,
            decidedAt: item.decided_at,
            decisionNote: item.decision_note,
            duration_type: item.duration_type ?? 'full_day',
            duration_hours: item.duration_hours ?? null,
          })));
        }
      }).catch(() => {});
  }, [user]);

  // ─── Load Pending ──────────────────────────────────────────────────────────
  const loadPending = useCallback(() => {
    if (!user || !canApprove(user.role)) return;
    fetch('/api/time-off/pending', { headers: getAuthHeaders() })
      .then(r => r.json())
      .then((d: any) => {
        if (Array.isArray(d?.items)) {
          setPending(d.items.map((item: any) => ({
            id: item.id,
            employeeName: item.user_name,
            employeeEmail: item.user_email,
            teamName: item.team_name,
            date: Array.isArray(item.dates) ? fmtDateRange(item.dates) : '—',
            dates: item.dates,
            reason: item.reason ?? '',
            submittedAt: item.created_at,
            duration_type: item.duration_type ?? 'full_day',
            duration_hours: item.duration_hours ?? null,
          })));
        }
      }).catch(() => {});
  }, [user]);

  // ─── Attendance Record (semi-monthly period) ───────────────────────────────
  const loadAttendanceRecord = useCallback(() => {
    if (!user) return;
    setAttendanceRecordLoading(true);
    fetch(`/api/time-off/attendance-record?period=${attendancePeriodView}`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject(new Error('Failed to load attendance record')))
      .then((d: any) => {
        if (d?.ok) {
          setAttendanceRecord({
            period: d.period,
            items: Array.isArray(d.items) ? d.items : [],
          });
        }
      })
      .catch(() => {
        setAttendanceRecord({
          period: {
            start: '',
            end: '',
            label: attendancePeriodView === 'current' ? 'Current pay period' : 'Previous pay period',
            kind: '10-24',
          },
          items: [],
        });
      })
      .finally(() => setAttendanceRecordLoading(false));
  }, [user, attendancePeriodView]);

  useEffect(() => {
    if (user) { loadMyRequests(); loadPending(); loadCalendar(); }
  }, [user]);

  useEffect(() => {
    if (user) loadAttendanceRecord();
  }, [user, attendancePeriodView, loadAttendanceRecord]);

  // ─── Load Balances ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    setBalancesLoading(true);
    if (isAdminLike(user.role)) {
      fetch('/api/time-off/all-balances', { headers: getAuthHeaders() })
        .then(r => r.json())
        .then((d: any) => {
          if (d?.ok && Array.isArray(d.employees)) {
            setAllBalances(d.employees.map((e: any) => ({ email: e.email, fullName: e.fullName, balances: normalizeBalances(e.balances) })));
          }
        }).catch(() => {}).finally(() => setBalancesLoading(false));
    } else {
      fetch('/api/time-off/balances', { headers: getAuthHeaders() })
        .then(r => r.json())
        .then((d: any) => {
          setBalancesLinked(d?.adpLinked !== false);
          if (d?.ok && Array.isArray(d.balances)) setMyBalances(normalizeBalances(d.balances));
        }).catch(() => {}).finally(() => setBalancesLoading(false));
    }
  }, [user]);

  // ─── Load Team Members ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!user || user.role !== 'TeamAdmin' || !user.teamId) return;
    fetch(`/api/teams/${user.teamId}/members`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then((d: any) => { if (Array.isArray(d?.members)) setTeamMembers(d.members); })
      .catch(() => {});
  }, [user]);

  // ─── Load Employees (for admin-insert) ─────────────────────────────────────
  useEffect(() => {
    if (!user || !isAdminLike(user.role)) return;
    fetch('/api/time-off/employees', { headers: getAuthHeaders() })
      .then(r => r.json())
      .then((d: any) => { if (Array.isArray(d?.employees)) setEmployees(d.employees); })
      .catch(() => {});
  }, [user]);

  // ─── Actions ───────────────────────────────────────────────────────────────
  async function submit() {
    try {
      if (durationType === 'custom') {
        const h = parseFloat(durationHours);
        if (isNaN(h) || h <= 0 || h > 24) {
          alert('Please enter a valid number of hours (0.5 – 24).');
          return;
        }
      }
      const parsed = CreateReq.parse({ dates: selected, reason });
      setSubmitting(true);
      const body: any = {
        dates: parsed.dates.map(d => d.toISOString().slice(0, 10)),
        reason,
        duration_type: durationType,
      };
      if (durationType === 'custom') body.duration_hours = parseFloat(durationHours);
      const res = await fetch('/api/time-off/requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.error || 'Failed to submit');
      }
      setSelected([]); setReason(''); setDurationType('full_day'); setDurationHours('');
      loadMyRequests(); loadCalendar();
      alert('Request submitted successfully!');
    } catch (e: any) {
      alert(e?.message || 'Failed to submit');
    } finally { setSubmitting(false); }
  }

  async function decide(id: string, decision: 'APPROVED' | 'REJECTED') {
    let note = '';
    if (decision === 'REJECTED') {
      const r = prompt('Please provide a reason for denying this request:');
      if (r === null) return;
      note = r.trim();
      if (!note) { alert('A reason is required when denying a request.'); return; }
    }
    await fetch(`/api/time-off/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ decision, note }),
    });
    loadPending(); loadCalendar();
  }

  // ─── Loading screen ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-10 w-10 text-yellow-400 animate-spin" />
          <p className="text-slate-400 text-sm">Loading your dashboard…</p>
        </div>
      </div>
    );
  }
  if (!user) return null;

  // ─── Nav Items ─────────────────────────────────────────────────────────────
  const navItems = [
    { id: 'dashboard',    label: 'Dashboard',        icon: Home },
    { id: 'request',      label: 'Request Time Off',  icon: CalendarDays },
    { id: 'my-requests',  label: 'My Requests',       icon: ClipboardList },
    ...(isEmployee(user.role) ? [{ id: 'balances', label: 'My Balances', icon: Wallet }] : []),
    ...(canApprove(user.role) ? [{ id: 'approvals', label: 'Approvals', icon: UserCheck, badge: pending.length }] : []),
    { id: 'attendance-record', label: 'Attendance Record', icon: Users },
    ...(isAdminLike(user.role) ? [{ id: 'all-balances', label: 'Team Balances', icon: TrendingUp }] : []),
    ...(isAdminLike(user.role) ? [{ id: 'admin-insert', label: 'Insert Time Off', icon: UserPlus }] : []),
    ...(user.role === 'TeamAdmin' ? [{ id: 'team', label: 'My Team', icon: Users }] : []),
    { id: 'holidays', label: 'Holidays', icon: CalendarOff },
  ];

  const roleLabel = user.role === 'TeamAdmin'
    ? `Team Admin${user.teamName ? ` — ${user.teamName}` : ''}`
    : user.role;
  const roleColor = {
    Admin:    'bg-purple-500/20 text-purple-300 border-purple-500/30',
    Manager:  'bg-blue-500/20 text-blue-300 border-blue-500/30',
    TeamAdmin:'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
    Closer:   'bg-orange-500/20 text-orange-300 border-orange-500/30',
  }[user.role] ?? 'bg-slate-700/50 text-slate-300 border-slate-600';

  // ─── Derived data (computed once per render, used in multiple panels) ──────
  const todayStr = new Date().toISOString().slice(0, 10);
  const approvedCalendar = calendar.filter(e =>
    e.status === 'APPROVED' &&
    Array.isArray(e.dates) &&
    e.dates.some(d => d >= todayStr)
  );
  const filteredRequests = myRequests.filter(r => statusFilter === 'ALL' || r.status === statusFilter);
  const filteredBalances = allBalances.filter(e =>
    searchQuery === '' ||
    e.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    e.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // ─── renderPanel — ALL panels inlined here (no nested component functions) ─
  // This pattern prevents React from treating panels as new component types on
  // each render, which was causing the textarea to lose focus on every keystroke.
  const renderPanel = () => {

    // ── Dashboard ──────────────────────────────────────────────────────────
    if (activeTab === 'dashboard') return (
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Welcome back, {user.name.split(' ')[0]}!</h1>
          <p className="text-slate-400 mt-1">
            {canApprove(user.role)
              ? user.role === 'TeamAdmin'
                ? `Managing ${user.teamName ?? 'your team'}`
                : 'Overview of all team activity and pending requests'
              : 'Manage your time off requests and check your balances'}
          </p>
        </div>
        {canApprove(user.role) && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Pending Requests"   value={pending.length}                                         icon={Clock}         color="bg-amber-500/15 text-amber-400" />
            <StatCard label="Approved (visible)" value={calendar.filter(e => e.status === 'APPROVED').length}  icon={CheckCircle2}  color="bg-emerald-500/15 text-emerald-400" />
            {user.role === 'TeamAdmin'
              ? <StatCard label="Team Members"   value={teamMembers.length}                                     icon={Users}         color="bg-blue-500/15 text-blue-400" />
              : <StatCard label="Employees"      value={allBalances.length}                                     icon={Users}         color="bg-blue-500/15 text-blue-400" />
            }
            <StatCard label="Upcoming Holidays"  value={upcomingHolidays.length}                               icon={CalendarOff}   color="bg-red-500/15 text-red-400" />
          </div>
        )}
        {/* Quick pending approvals */}
        {canApprove(user.role) && pending.length > 0 && (
          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl p-6">
            <SectionHeader icon={Clock} title="Pending Requests" subtitle={`${pending.length} request${pending.length !== 1 ? 's' : ''} awaiting your decision`} color="text-amber-400" />
            <div className="space-y-3">
              {pending.slice(0, 5).map(item => (
                <div key={item.id} className="flex items-center justify-between p-4 rounded-xl bg-[#111] border border-[#2a2a2a] gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-9 h-9 rounded-full bg-gradient-to-br ${avatarColor(item.employeeName || '?')} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}>
                      {initials(item.employeeName || '?')}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-white">{item.employeeName || item.employeeEmail}</div>
                      <div className="text-xs text-slate-400">{item.date}{item.teamName ? ` · ${item.teamName}` : ''}</div>
                      <div className="text-xs text-slate-500 truncate">{item.reason}</div>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => decide(item.id, 'APPROVED')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 text-xs font-semibold hover:bg-emerald-500/25 transition-colors">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                    </button>
                    <button onClick={() => decide(item.id, 'REJECTED')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/15 text-red-400 border border-red-500/30 text-xs font-semibold hover:bg-red-500/25 transition-colors">
                      <XCircle className="h-3.5 w-3.5" /> Deny
                    </button>
                  </div>
                </div>
              ))}
              {pending.length > 5 && (
                <button onClick={() => setActiveTab('approvals')} className="w-full text-center text-sm text-yellow-400 hover:text-yellow-300 py-2">
                  View all {pending.length} requests →
                </button>
              )}
            </div>
          </div>
        )}
        {canApprove(user.role) && pending.length === 0 && (
          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl p-8 flex items-center gap-4">
            <CheckCircle2 className="h-10 w-10 text-emerald-400 flex-shrink-0" />
            <div>
              <h3 className="text-base font-semibold text-white">All caught up!</h3>
              <p className="text-slate-400 text-sm">No pending requests at this time.</p>
            </div>
          </div>
        )}
        {/* Team Calendar */}
        {calendarLoading ? (
          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl p-12 flex items-center justify-center">
            <Loader2 className="h-8 w-8 text-yellow-400 animate-spin" />
          </div>
        ) : (
          <TeamCalendarView calendar={calendar} />
        )}
      </div>
    );

    // ── Request Time Off ───────────────────────────────────────────────────
    if (activeTab === 'request') return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Request Time Off</h1>
          <p className="text-slate-400 mt-1">Select dates and submit your request for approval</p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: calendar picker + form */}
          <div className="space-y-4">
            <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl p-6">
              <SectionHeader icon={CalendarDays} title="Select Dates" subtitle="Click to select one or more days" />
              <div className="flex justify-center">
                <DayPicker
                  mode="multiple"
                  selected={selected}
                  onSelect={(days) => setSelected(days ?? [])}
                  disabled={{ before: new Date() }}
                  className="rdp-dark"
                />
              </div>
              {selected.length > 0 && (
                <div className="mt-4 p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
                  <p className="text-sm text-yellow-300 font-medium">
                    {selected.length} day{selected.length !== 1 ? 's' : ''} selected:{' '}
                    {selected.sort((a, b) => a.getTime() - b.getTime()).map(d => fmtMDY(d.toISOString())).join(', ')}
                  </p>
                </div>
              )}
            </div>
            <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl p-6 space-y-4">
              <SectionHeader icon={ClipboardList} title="Request Details" />
              {/* Duration selector */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Absence Duration</label>
                <div className="flex gap-2">
                  {(['full_day', 'half_day', 'custom'] as const).map(dt => (
                    <button
                      key={dt}
                      type="button"
                      onClick={() => { setDurationType(dt); if (dt !== 'custom') setDurationHours(''); }}
                      className={`flex-1 py-2 px-3 rounded-xl text-sm font-medium border transition-colors ${
                        durationType === dt
                          ? 'bg-yellow-500 text-black border-yellow-500'
                          : 'bg-[#111] text-slate-300 border-[#2a2a2a] hover:border-yellow-500/40'
                      }`}
                    >
                      {dt === 'full_day' ? '☀️ Full Day' : dt === 'half_day' ? '🌤 Half Day' : '⏱ Custom'}
                    </button>
                  ))}
                </div>
                {durationType === 'half_day' && (
                  <p className="text-xs text-slate-500 mt-2">Half day = 4 hours of absence</p>
                )}
                {durationType === 'custom' && (
                  <div className="mt-2">
                    <input
                      type="number"
                      min="0.5"
                      max="24"
                      step="0.5"
                      placeholder="Hours absent (e.g. 2.5)"
                      value={durationHours}
                      onChange={e => setDurationHours(e.target.value)}
                      className="w-full rounded-xl border border-[#2a2a2a] bg-[#111] text-slate-200 placeholder-slate-500 p-3 text-sm focus:outline-none focus:border-yellow-500/50 focus:ring-1 focus:ring-yellow-500/20 transition-colors"
                    />
                    <p className="text-xs text-slate-500 mt-1">Enter hours between 0.5 and 24</p>
                  </div>
                )}
              </div>
              {/* Reason textarea */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Reason for Time Off</label>
                <textarea
                  placeholder="Please provide a reason for your time off request…"
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  rows={4}
                  className="w-full rounded-xl border border-[#2a2a2a] bg-[#111] text-slate-200 placeholder-slate-500 p-3 text-sm resize-none focus:outline-none focus:border-yellow-500/50 focus:ring-1 focus:ring-yellow-500/20 transition-colors"
                />
                <p className="text-xs text-slate-500 mt-1">{reason.length}/500 characters (minimum 3)</p>
              </div>
              <button
                onClick={submit}
                disabled={submitting || selected.length === 0 || reason.trim().length < 3 || (durationType === 'custom' && (!durationHours || parseFloat(durationHours) <= 0))}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-yellow-500 text-black font-semibold text-sm hover:bg-yellow-400 disabled:opacity-40 disabled:pointer-events-none transition-colors"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <SendHorizonal className="h-4 w-4" />}
                {submitting ? 'Submitting…' : 'Submit Request'}
              </button>
            </div>
          </div>
          {/* Right: upcoming approved */}
          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl p-6">
            <SectionHeader icon={CheckCircle2} title="Upcoming Approved Time Off" color="text-emerald-400" />
            {approvedCalendar.length === 0 ? (
              <p className="text-sm text-slate-500">Nothing scheduled yet.</p>
            ) : (
              <ul className="space-y-2 max-h-96 overflow-y-auto">
                {approvedCalendar
                  .sort((a, b) => (a.dates[0] || '') < (b.dates[0] || '') ? -1 : 1)
                  .slice(0, 15)
                  .map((e, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm p-2 rounded-lg hover:bg-[#111] transition-colors">
                      <div className={`w-7 h-7 rounded-full bg-gradient-to-br ${avatarColor(e.userName)} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}>
                        {initials(e.userName)}
                      </div>
                      <div>
                        <span className="font-medium text-slate-300">{e.userName}</span>
                        <span className="text-slate-500"> · {fmtDateRange(e.dates)}</span>
                      </div>
                    </li>
                  ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    );

    // ── My Requests ────────────────────────────────────────────────────────
    if (activeTab === 'my-requests') return (
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">My Requests</h1>
            <p className="text-slate-400 mt-1">Track all your time off requests</p>
          </div>
          <div className="flex gap-2">
            {(['ALL', 'PENDING', 'APPROVED', 'REJECTED'] as const).map(s => (
              <button key={s} onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${statusFilter === s ? 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30' : 'bg-[#1a1a1a] text-slate-400 border-[#2a2a2a] hover:border-slate-600'}`}>
                {s}
              </button>
            ))}
          </div>
        </div>
        <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl p-6">
          {filteredRequests.length === 0 ? (
            <EmptyState message="No requests found." />
          ) : (
            <ul className="space-y-3">
              {filteredRequests.map(req => (
                <li key={req.id} className="p-4 rounded-xl border border-[#2a2a2a] bg-[#111]">
                  <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <StatusBadge status={req.status} />
                      <span className="text-sm font-semibold text-white">{req.date}</span>
                      <DurationBadge duration_type={req.duration_type} duration_hours={req.duration_hours} />
                    </div>
                    <span className="text-xs text-slate-500">{req.submittedAt ? `Submitted: ${fmtTimeLocal(req.submittedAt)}` : ''}</span>
                  </div>
                  <div className="text-sm text-slate-300"><span className="text-slate-500">Reason: </span>{req.reason || '—'}</div>
                  {req.status === 'REJECTED' && req.decisionNote && (
                    <div className="mt-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20">
                      <div className="text-xs font-semibold text-red-400 mb-1">Denial Reason:</div>
                      <div className="text-sm text-slate-300">{req.decisionNote}</div>
                    </div>
                  )}
                  {req.decidedAt && <div className="text-xs text-slate-500 mt-1">Decided: {fmtTimeLocal(req.decidedAt)}</div>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );

    // ── My Balances (employees) ────────────────────────────────────────────
    if (activeTab === 'balances') return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-white">My Balances</h1>
          <p className="text-slate-400 mt-1">Your current time off balances from ADP</p>
        </div>
        {balancesLoading ? (
          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl p-12 flex items-center justify-center">
            <Loader2 className="h-8 w-8 text-yellow-400 animate-spin" />
          </div>
        ) : !balancesLinked ? (
          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl p-8 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-500/15 flex items-center justify-center flex-shrink-0">
              <Wallet className="h-6 w-6 text-amber-400" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-white">ADP Not Linked</h3>
              <p className="text-slate-400 text-sm">Your account is not yet linked to ADP. Contact HR to get your balances set up.</p>
            </div>
          </div>
        ) : myBalances.length === 0 ? (
          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl p-8">
            <EmptyState message="No balance data available. Contact HR if you believe this is an error." />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {myBalances.map((b, i) => (
              <div key={i} className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-yellow-500/15 flex items-center justify-center flex-shrink-0">
                    <Wallet className="h-5 w-5 text-yellow-400" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white">{b.timeOffTypeName}</div>
                    <div className="text-xs text-slate-500">{b.units}</div>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Available</span>
                    <span className="font-bold text-emerald-400">{b.balance} {b.units}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Used</span>
                    <span className="font-medium text-slate-300">{b.used} {b.units}</span>
                  </div>
                  <div className="w-full bg-[#2a2a2a] rounded-full h-2 mt-3">
                    <div
                      className="bg-yellow-500 h-2 rounded-full transition-all"
                      style={{ width: `${Math.min(100, b.balance + b.used > 0 ? (b.balance / (b.balance + b.used)) * 100 : 0)}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );

    // ── Team Balances (admins/managers) ────────────────────────────────────
    if (activeTab === 'all-balances') return (
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Team Balances</h1>
            <p className="text-slate-400 mt-1">ADP time off balances for all employees</p>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <input
              type="text"
              placeholder="Search employees…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-9 pr-4 py-2 rounded-xl border border-[#2a2a2a] bg-[#1a1a1a] text-slate-200 placeholder-slate-500 text-sm focus:outline-none focus:border-yellow-500/50 transition-colors w-64"
            />
          </div>
        </div>
        {balancesLoading ? (
          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl p-12 flex items-center justify-center">
            <Loader2 className="h-8 w-8 text-yellow-400 animate-spin" />
          </div>
        ) : filteredBalances.length === 0 ? (
          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl p-8">
            <EmptyState message={searchQuery ? 'No employees match your search.' : 'No balance data available.'} />
          </div>
        ) : (
          <div className="space-y-3">
            {filteredBalances.map((emp, i) => (
              <div key={i} className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${avatarColor(emp.fullName)} flex items-center justify-center text-white font-bold text-sm flex-shrink-0`}>
                    {initials(emp.fullName)}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white">{emp.fullName}</div>
                    <div className="text-xs text-slate-500">{emp.email}</div>
                  </div>
                </div>
                {emp.balances.length === 0 ? (
                  <p className="text-xs text-slate-500">No balance data</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {emp.balances.map((b, j) => (
                      <div key={j} className="bg-[#111] rounded-xl p-3 border border-[#2a2a2a]">
                        <div className="text-xs text-slate-500 mb-1 truncate">{b.timeOffTypeName}</div>
                        <div className="text-base font-bold text-emerald-400">{b.balance}</div>
                        <div className="text-xs text-slate-600">{b.units} avail.</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );

    // ── Approvals (managers/admins) ────────────────────────────────────────
    if (activeTab === 'approvals') return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Approvals</h1>
          <p className="text-slate-400 mt-1">
            {pending.length > 0
              ? `${pending.length} request${pending.length !== 1 ? 's' : ''} awaiting your decision`
              : 'No pending requests'}
          </p>
        </div>
        <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl p-6">
          {pending.length === 0 ? (
            <div className="flex items-center gap-4 py-4">
              <CheckCircle2 className="h-10 w-10 text-emerald-400 flex-shrink-0" />
              <div>
                <h3 className="text-base font-semibold text-white">All caught up!</h3>
                <p className="text-slate-400 text-sm">No pending requests at this time.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {pending.map(item => (
                <div key={item.id} className="p-5 rounded-xl border border-[#2a2a2a] bg-[#111]">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${avatarColor(item.employeeName || '?')} flex items-center justify-center text-white text-sm font-bold flex-shrink-0`}>
                        {initials(item.employeeName || '?')}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-white">{item.employeeName || item.employeeEmail}</div>
                        {item.employeeEmail && item.employeeName && (
                          <div className="text-xs text-slate-500">{item.employeeEmail}</div>
                        )}
                        {item.teamName && <div className="text-xs text-slate-500">{item.teamName}</div>}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => decide(item.id, 'APPROVED')} className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 text-sm font-semibold hover:bg-emerald-500/25 transition-colors">
                        <CheckCircle2 className="h-4 w-4" /> Approve
                      </button>
                      <button onClick={() => decide(item.id, 'REJECTED')} className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-red-500/15 text-red-400 border border-red-500/30 text-sm font-semibold hover:bg-red-500/25 transition-colors">
                        <XCircle className="h-4 w-4" /> Deny
                      </button>
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                    <div>
                      <div className="text-xs text-slate-500 mb-1">Dates</div>
                      <div className="text-slate-300 font-medium">{item.date}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 mb-1">Duration</div>
                      <DurationBadge duration_type={item.duration_type} duration_hours={item.duration_hours} />
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 mb-1">Submitted</div>
                      <div className="text-slate-400 text-xs">{item.submittedAt ? fmtTimeLocal(item.submittedAt) : '—'}</div>
                    </div>
                  </div>
                  {item.reason && (
                    <div className="mt-3 p-3 rounded-xl bg-[#1a1a1a] border border-[#2a2a2a]">
                      <div className="text-xs text-slate-500 mb-1">Reason</div>
                      <div className="text-sm text-slate-300">{item.reason}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );

    // ── Attendance Record (semi-monthly pay period) ────────────────────────
    if (activeTab === 'attendance-record') return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Attendance Record</h1>
          <p className="text-slate-400 mt-1">Missed day totals by pay period.</p>
        </div>
        <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl p-6">
          <div className="mb-4 flex flex-wrap gap-2">
            <button
              onClick={() => setAttendancePeriodView('previous')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                attendancePeriodView === 'previous'
                  ? 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30'
                  : 'bg-[#111] text-slate-400 border-[#2a2a2a] hover:border-slate-600'
              }`}
            >
              Previous Pay Period
            </button>
            <button
              onClick={() => setAttendancePeriodView('current')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                attendancePeriodView === 'current'
                  ? 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30'
                  : 'bg-[#111] text-slate-400 border-[#2a2a2a] hover:border-slate-600'
              }`}
            >
              Current Pay Period
            </button>
          </div>
          <SectionHeader
            icon={Users}
            title={attendanceRecord?.period?.label || 'Current pay period'}
            subtitle={`Period type: ${attendanceRecord?.period?.kind === '25-9' ? '25th–9th' : '10th–24th'}`}
            color="text-blue-400"
          />
          {attendanceRecordLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 text-yellow-400 animate-spin" />
            </div>
          ) : !attendanceRecord || attendanceRecord.items.length === 0 ? (
            <EmptyState message="No absences recorded for this pay period." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-400 border-b border-[#2a2a2a]">
                    <th className="py-3 pr-3 font-semibold">Employee Name</th>
                    <th className="py-3 pr-3 font-semibold">Email</th>
                    <th className="py-3 text-right font-semibold">Missed Days</th>
                  </tr>
                </thead>
                <tbody>
                  {attendanceRecord.items.map((row) => (
                    <tr key={row.employee_email} className="border-b border-[#1f1f1f]">
                      <td className="py-3 pr-3 text-white font-medium">{row.employee_name}</td>
                      <td className="py-3 pr-3 text-slate-400">{row.employee_email}</td>
                      <td className="py-3 text-right text-yellow-400 font-bold">{row.missed_days}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );

    // ── My Team (TeamAdmin) ────────────────────────────────────────────────
    if (activeTab === 'team') return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-white">My Team</h1>
          <p className="text-slate-400 mt-1">{user.teamName ?? 'Your team'} · {teamMembers.length} member{teamMembers.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl p-6">
          {teamMembers.length === 0 ? (
            <EmptyState message="No team members found." />
          ) : (
            <div className="space-y-3">
              {teamMembers.map(member => (
                <div key={member.id} className="flex items-center gap-4 p-4 rounded-xl border border-[#2a2a2a] bg-[#111]">
                  <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${avatarColor(member.full_name || member.email)} flex items-center justify-center text-white font-bold text-sm flex-shrink-0`}>
                    {initials(member.full_name || member.email)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-white truncate">{member.full_name || '—'}</div>
                    <div className="text-xs text-slate-500 truncate">{member.email}</div>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                    member.role === 'TeamAdmin'
                      ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30'
                      : 'bg-slate-700/50 text-slate-300 border-slate-600'
                  }`}>
                    {member.role}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );

    // ── Holidays ───────────────────────────────────────────────────────────
    if (activeTab === 'holidays') return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Company Holidays</h1>
          <p className="text-slate-400 mt-1">Observed holidays for 2025 and 2026</p>
        </div>
        {[2025, 2026].map(year => {
          const holidays = ALL_HOLIDAYS.filter(h => h.year === year);
          return (
            <div key={year} className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl p-6">
              <SectionHeader icon={CalendarOff} title={`${year} Holidays`} subtitle={`${holidays.length} observed holidays`} color="text-red-400" />
              <div className="space-y-2">
                {holidays.map((h, i) => {
                  const isPast = h.date < today;
                  const isUpcoming = !isPast && h.date.getTime() - today.getTime() < 30 * 864e5;
                  return (
                    <div key={i} className={`flex items-center justify-between p-4 rounded-xl border transition-colors ${
                      isPast
                        ? 'border-[#1e1e1e] bg-[#111]/50 opacity-50'
                        : isUpcoming
                        ? 'border-yellow-500/30 bg-yellow-500/5'
                        : 'border-[#2a2a2a] bg-[#111]'
                    }`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isPast ? 'bg-[#2a2a2a]' : 'bg-red-500/15'}`}>
                          <CalendarOff className={`h-4 w-4 ${isPast ? 'text-slate-600' : 'text-red-400'}`} />
                        </div>
                        <div>
                          <div className={`text-sm font-semibold ${isPast ? 'text-slate-500' : 'text-white'}`}>{h.name}</div>
                          <div className="text-xs text-slate-500">{h.dayOfWeek}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-sm font-medium ${isPast ? 'text-slate-600' : 'text-slate-300'}`}>
                          {h.date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
                        </div>
                        {isUpcoming && (
                          <div className="text-xs text-yellow-400 font-medium">Coming up!</div>
                        )}
                        {isPast && (
                          <div className="text-xs text-slate-600">Past</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );

    // ── Admin Insert Time Off ──────────────────────────────────────────────
    if (activeTab === 'admin-insert') {
      const filteredEmployees = employees.filter(e =>
        adminInsertSearch === '' ||
        (e.full_name || '').toLowerCase().includes(adminInsertSearch.toLowerCase()) ||
        e.email.toLowerCase().includes(adminInsertSearch.toLowerCase())
      );

      async function submitAdminInsert() {
        if (!adminInsertEmployee) { alert('Please select an employee.'); return; }
        if (adminInsertSelected.length === 0) { alert('Please select at least one date.'); return; }
        if (adminInsertReason.trim().length < 3) { alert('Please enter a reason (minimum 3 characters).'); return; }
        if (adminInsertDurationType === 'custom') {
          const h = parseFloat(adminInsertDurationHours);
          if (isNaN(h) || h <= 0 || h > 24) { alert('Please enter a valid number of hours (0.5 – 24).'); return; }
        }
        setAdminInsertSubmitting(true);
        try {
          const body: any = {
            employee_id: adminInsertEmployee,
            dates: adminInsertSelected.map(d => d.toISOString().slice(0, 10)),
            reason: adminInsertReason.trim(),
            duration_type: adminInsertDurationType,
            status: adminInsertStatus,
          };
          if (adminInsertDurationType === 'custom') body.duration_hours = parseFloat(adminInsertDurationHours);
          const res = await fetch('/api/time-off/admin-insert', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            body: JSON.stringify(body),
          });
          if (!res.ok) {
            const j = await res.json().catch(() => ({}));
            throw new Error(j?.error || 'Failed to insert time off');
          }
          setAdminInsertEmployee('');
          setAdminInsertSelected([]);
          setAdminInsertReason('');
          setAdminInsertDurationType('full_day');
          setAdminInsertDurationHours('');
          setAdminInsertStatus('APPROVED');
          setAdminInsertSearch('');
          loadCalendar();
          const emp = employees.find(e => e.id === adminInsertEmployee);
          alert(`Time off inserted successfully for ${emp?.full_name || emp?.email || 'employee'}.`);
        } catch (e: any) {
          alert(e?.message || 'Failed to insert time off');
        } finally {
          setAdminInsertSubmitting(false);
        }
      }

      return (
        <div className="space-y-6 w-full">
          <div>
            <h1 className="text-2xl font-bold text-white">Insert Time Off</h1>
            <p className="text-slate-400 mt-1">Insert a time-off entry on behalf of an employee</p>
          </div>

          {/* Employee selector */}
          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl p-6 space-y-4">
              <SectionHeader icon={UserPlus} title="Select Employee" subtitle="Search and select the employee" color="text-purple-400" />
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <input
                  type="text"
                  placeholder="Search by name or email…"
                  value={adminInsertSearch}
                  onChange={e => setAdminInsertSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-[#2a2a2a] bg-[#111] text-slate-200 placeholder-slate-500 text-sm focus:outline-none focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/20 transition-colors"
                />
              </div>
              <div className="max-h-56 overflow-y-auto space-y-1 rounded-xl border border-[#2a2a2a] bg-[#111] p-2">
                {filteredEmployees.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No employees found</p>
                ) : (
                  filteredEmployees.map(emp => (
                    <button
                      key={emp.id}
                      type="button"
                      onClick={() => setAdminInsertEmployee(emp.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${
                        adminInsertEmployee === emp.id
                          ? 'bg-purple-500/20 border border-purple-500/30'
                          : 'hover:bg-[#1a1a1a] border border-transparent'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${avatarColor(emp.full_name || emp.email)} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}>
                        {initials(emp.full_name || emp.email)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-white truncate">{emp.full_name || emp.email}</div>
                        <div className="text-xs text-slate-500 truncate">{emp.email}{emp.team_name ? ` · ${emp.team_name}` : ''}</div>
                      </div>
                      {adminInsertEmployee === emp.id && (
                        <CheckCircle2 className="h-4 w-4 text-purple-400 flex-shrink-0" />
                      )}
                    </button>
                  ))
                )}
              </div>
              {adminInsertEmployee && (() => {
                const emp = employees.find(e => e.id === adminInsertEmployee);
                return emp ? (
                  <div className="flex items-center gap-2 p-3 rounded-xl bg-purple-500/10 border border-purple-500/20">
                    <CheckCircle2 className="h-4 w-4 text-purple-400 flex-shrink-0" />
                    <span className="text-sm text-purple-300 font-medium">{emp.full_name || emp.email}</span>
                  </div>
                ) : null;
              })()}
          </div>

          {/* Request details */}
          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl p-6 space-y-4">
              <SectionHeader icon={ClipboardList} title="Request Details" />

              {/* Duration selector */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Absence Duration</label>
                <div className="flex gap-2">
                  {(['full_day', 'half_day', 'custom'] as const).map(dt => (
                    <button
                      key={dt}
                      type="button"
                      onClick={() => { setAdminInsertDurationType(dt); if (dt !== 'custom') setAdminInsertDurationHours(''); }}
                      className={`flex-1 py-2 px-3 rounded-xl text-sm font-medium border transition-colors ${
                        adminInsertDurationType === dt
                          ? 'bg-yellow-500 text-black border-yellow-500'
                          : 'bg-[#111] text-slate-300 border-[#2a2a2a] hover:border-yellow-500/40'
                      }`}
                    >
                      {dt === 'full_day' ? '☀️ Full Day' : dt === 'half_day' ? '🌤 Half Day' : '⏱ Custom'}
                    </button>
                  ))}
                </div>
                {adminInsertDurationType === 'half_day' && (
                  <p className="text-xs text-slate-500 mt-2">Half day = 4 hours of absence</p>
                )}
                {adminInsertDurationType === 'custom' && (
                  <div className="mt-2">
                    <input
                      type="number"
                      min="0.5"
                      max="24"
                      step="0.5"
                      placeholder="Hours absent (e.g. 2.5)"
                      value={adminInsertDurationHours}
                      onChange={e => setAdminInsertDurationHours(e.target.value)}
                      className="w-full rounded-xl border border-[#2a2a2a] bg-[#111] text-slate-200 placeholder-slate-500 p-3 text-sm focus:outline-none focus:border-yellow-500/50 focus:ring-1 focus:ring-yellow-500/20 transition-colors"
                    />
                    <p className="text-xs text-slate-500 mt-1">Enter hours between 0.5 and 24</p>
                  </div>
                )}
              </div>

              {/* Reason textarea */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Reason for Time Off</label>
                <textarea
                  placeholder="Enter the reason for this time off…"
                  value={adminInsertReason}
                  onChange={e => setAdminInsertReason(e.target.value)}
                  rows={4}
                  className="w-full rounded-xl border border-[#2a2a2a] bg-[#111] text-slate-200 placeholder-slate-500 p-3 text-sm resize-none focus:outline-none focus:border-yellow-500/50 focus:ring-1 focus:ring-yellow-500/20 transition-colors"
                />
                <p className="text-xs text-slate-500 mt-1">{adminInsertReason.length}/500 characters (minimum 3)</p>
              </div>

              {/* Status selector */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Insert Status</label>
                <div className="flex gap-2">
                  {(['APPROVED', 'PENDING'] as const).map(s => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setAdminInsertStatus(s)}
                      className={`flex-1 py-2 px-3 rounded-xl text-sm font-medium border transition-colors ${
                        adminInsertStatus === s
                          ? s === 'APPROVED'
                            ? 'bg-emerald-500 text-white border-emerald-500'
                            : 'bg-amber-500 text-black border-amber-500'
                          : 'bg-[#111] text-slate-300 border-[#2a2a2a] hover:border-slate-500'
                      }`}
                    >
                      {s === 'APPROVED' ? '✅ Approved' : '⏳ Pending'}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  {adminInsertStatus === 'APPROVED'
                    ? 'Request will be immediately approved and a calendar invite will be sent.'
                    : 'Request will be created as pending and will require approval.'}
                </p>
              </div>
          </div>

          {/* Row 2: Date picker — full width, calendar centered */}
          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl p-6">
            <SectionHeader icon={CalendarDays} title="Select Dates" subtitle="Click to select one or more days" />
            <div className="flex justify-center mt-2">
              <DayPicker
                mode="multiple"
                selected={adminInsertSelected}
                onSelect={(days) => setAdminInsertSelected(days ?? [])}
                className="rdp-dark"
              />
            </div>
            {adminInsertSelected.length > 0 && (
              <div className="mt-4 p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
                <p className="text-sm text-yellow-300 font-medium">
                  {adminInsertSelected.length} day{adminInsertSelected.length !== 1 ? 's' : ''} selected:{' '}
                  {adminInsertSelected.sort((a, b) => a.getTime() - b.getTime()).map(d => fmtMDY(d.toISOString())).join(', ')}
                </p>
              </div>
            )}
          </div>

          {/* Row 3: Summary + Submit */}
          <div className="space-y-4">
            {/* Summary card */}
            <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl p-6">
              <SectionHeader icon={CheckCircle2} title="Summary" color="text-emerald-400" />
              {adminInsertEmployee && adminInsertSelected.length > 0 ? (() => {
                const emp = employees.find(e => e.id === adminInsertEmployee);
                return (
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between gap-2">
                      <span className="text-slate-500 flex-shrink-0">Employee</span>
                      <span className="text-white font-medium text-right truncate">{emp?.full_name || emp?.email}</span>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="text-slate-500 flex-shrink-0">Dates</span>
                      <span className="text-slate-300 text-right">{fmtDateRange(adminInsertSelected.map(d => d.toISOString().slice(0, 10)))}</span>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="text-slate-500 flex-shrink-0">Days</span>
                      <span className="text-slate-300">{adminInsertSelected.length}</span>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="text-slate-500 flex-shrink-0">Duration</span>
                      <span className="text-slate-300">{adminInsertDurationType === 'full_day' ? 'Full Day' : adminInsertDurationType === 'half_day' ? 'Half Day' : `${adminInsertDurationHours}h`}</span>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="text-slate-500 flex-shrink-0">Status</span>
                      <StatusBadge status={adminInsertStatus} />
                    </div>
                  </div>
                );
              })() : (
                <p className="text-sm text-slate-500">Select an employee and dates above to see a summary.</p>
              )}
            </div>

            {/* Submit */}
            <div className="flex items-end">
              <button
                onClick={submitAdminInsert}
                disabled={adminInsertSubmitting || !adminInsertEmployee || adminInsertSelected.length === 0 || adminInsertReason.trim().length < 3 || (adminInsertDurationType === 'custom' && (!adminInsertDurationHours || parseFloat(adminInsertDurationHours) <= 0))}
                className="w-full flex items-center justify-center gap-2 px-4 py-4 rounded-2xl bg-purple-500 text-white font-semibold text-base hover:bg-purple-400 disabled:opacity-40 disabled:pointer-events-none transition-colors"
              >
                {adminInsertSubmitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <UserPlus className="h-5 w-5" />}
                {adminInsertSubmitting ? 'Inserting…' : 'Insert Time Off'}
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Fallback
    return null;
  };

  // ─── Layout ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#0a0a0a] flex overflow-x-hidden w-full">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/60 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#111111] border-r border-[#2a2a2a] flex flex-col transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 lg:static lg:flex`}>
        <div className="flex items-center gap-3 px-5 py-5 border-b border-[#2a2a2a]">
          <div className="w-9 h-9 rounded-xl bg-yellow-500/20 flex items-center justify-center">
            <CalendarDays className="h-5 w-5 text-yellow-400" />
          </div>
          <div>
            <div className="text-sm font-bold text-white">Time Off</div>
            <div className="text-xs text-slate-500">Timeshare Help Center</div>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="ml-auto lg:hidden text-slate-500 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="px-4 py-4 border-b border-[#2a2a2a]">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-full bg-gradient-to-br ${avatarColor(user.name)} flex items-center justify-center text-white font-bold text-sm flex-shrink-0`}>
              {initials(user.name)}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-white truncate">{user.name}</div>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${roleColor}`}>
                {roleLabel}
              </span>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navItems.map(item => (
            <button
              key={item.id}
              onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                activeTab === item.id
                  ? 'bg-yellow-500/15 text-yellow-400 border border-yellow-500/20'
                  : 'text-slate-400 hover:text-white hover:bg-[#1e1e1e]'
              }`}
            >
              <item.icon className="h-4 w-4 flex-shrink-0" />
              <span className="flex-1 text-left">{item.label}</span>
              {(item as any).badge > 0 && (
                <span className="bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {(item as any).badge}
                </span>
              )}
            </button>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-[#2a2a2a]">
          <button
            onClick={() => { clearToken(); window.location.href = '/api/auth/logout'; }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-all"
          >
            <LogOut className="h-4 w-4" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-x-hidden">
        <header className="sticky top-0 z-30 bg-[#0a0a0a]/80 backdrop-blur border-b border-[#1e1e1e] px-4 lg:px-8 py-4 flex items-center justify-between gap-4">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-slate-400 hover:text-white transition-colors">
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-sm text-slate-500 hidden sm:block">
              {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: AZ_TZ })}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {notifCount > 0 && (
              <button onClick={() => setActiveTab('approvals')} className="relative p-2 rounded-xl bg-[#1a1a1a] border border-[#2a2a2a] text-slate-400 hover:text-white transition-colors">
                <Bell className="h-5 w-5" />
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center">{notifCount}</span>
              </button>
            )}
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#1a1a1a] border border-[#2a2a2a]">
              <div className={`w-6 h-6 rounded-full bg-gradient-to-br ${avatarColor(user.name)} flex items-center justify-center text-white font-bold text-xs`}>
                {initials(user.name)}
              </div>
              <span className="text-sm font-medium text-slate-300 hidden sm:block">{user.name.split(' ')[0]}</span>
            </div>
          </div>
        </header>
        <main className="flex-1 px-4 lg:px-8 py-8">
          {renderPanel()}
        </main>
      </div>
    </div>
  );
}
